import type { Camper } from '@yescapa-dev/ysc-api-js/legacy'
import { BOOKING_HOUR_FROM_AFTERNOON, BOOKING_HOUR_FROM_MORNING, BOOKING_HOUR_TO_AFTERNOON, BOOKING_HOUR_TO_MORNING, BOOKING_KM_OPTIONS } from '@yescapa-dev/ysc-api-js/modern'
import { isValid, parse } from 'date-fns'

export const useCamperQuery = (camper: Ref<Camper | null>) => {
  const route = useRoute()

  // dateFrom
  const queryDateFrom = computed(() => {
    const dateFrom = queryValueToString(route.query.date_from)

    if (!isDefined(dateFrom)) {
      return undefined
    }
    const date = parse(dateFrom, 'yyyy-MM-dd', new Date())

    return isValid(date) ? dateFrom : undefined
  })

  const queryDateFromCoerced = computed(() => {
    if (!isDefinedRef(queryDateFrom)) {
      return undefined
    }

    return parse(queryDateFrom.value, 'yyyy-MM-dd', new Date())
  })

  // dateTo
  const queryDateTo = computed(() => {
    const dateTo = queryValueToString(route.query.date_to)

    if (!isDefined(dateTo)) {
      return undefined
    }
    const date = parse(dateTo, 'yyyy-MM-dd', new Date())

    return isValid(date) ? dateTo : undefined
  })

  const queryDateToCoerced = computed(() => {
    if (!isDefinedRef(queryDateTo)) {
      return undefined
    }

    return parse(queryDateTo.value, 'yyyy-MM-dd', new Date())
  })

  // hourFrom
  const queryHourFrom = computed(() => {
    const hourFrom = queryValueToNumber(route.query.hour_from)
    try {
      assertHourFrom(hourFrom)
    }
    catch (_e) {
      return undefined
    }

    return hourFrom
  })

  const queryHourFromCoerced = computed(() => {
    if (!isDefinedRef(camper)) {
      return BOOKING_HOUR_FROM_MORNING
    }

    if (camper.value.has_forced_morning_departure) {
      return BOOKING_HOUR_FROM_MORNING
    }

    if (camper.value.has_forced_afternoon_departure) {
      return BOOKING_HOUR_FROM_AFTERNOON
    }

    if (isDefinedRef(queryHourFrom)) {
      return queryHourFrom.value
    }

    if (camper.value.half_day_activated) {
      return BOOKING_HOUR_FROM_AFTERNOON
    }

    return BOOKING_HOUR_FROM_MORNING
  })

  // hourTo
  const queryHourTo = computed(() => {
    const hourTo = queryValueToNumber(route.query.hour_to)
    try {
      assertHourTo(hourTo)
    }
    catch (_e) {
      return undefined
    }

    return hourTo
  })

  const queryHourToCoerced = computed(() => {
    if (!isDefinedRef(camper)) {
      return BOOKING_HOUR_TO_AFTERNOON
    }

    if (camper.value.has_forced_morning_arrival) {
      return BOOKING_HOUR_TO_MORNING
    }

    if (camper.value.has_forced_afternoon_arrival) {
      return BOOKING_HOUR_TO_AFTERNOON
    }

    if (isDefinedRef(queryHourTo)) {
      return queryHourTo.value
    }

    if (camper.value.half_day_activated) {
      return BOOKING_HOUR_TO_MORNING
    }

    return BOOKING_HOUR_TO_AFTERNOON
  })

  // kilometers
  const queryKilometers = computed(() => {
    const kilometers = queryValueToNumber(route.query.kilometers)
    try {
      assertKilometersOption(kilometers)
    }
    catch (_e) {
      return undefined
    }
    return kilometers
  })

  const queryKilometersCoerced = computed(() => {
    if (!isDefinedRef(camper)) {
      return BOOKING_KM_OPTIONS.KM_100
    }

    let kilometers = queryKilometers.value

    if (!camper.value.unlimited_km_allowed && kilometers === BOOKING_KM_OPTIONS.UNLIMITED) {
      kilometers = undefined
    }

    if (camper.value.unlimited_km_allowed && camper.value.price_km_unlimited === 0) {
      return BOOKING_KM_OPTIONS.UNLIMITED
    }

    if (camper.value.price_km_200 === 0 && kilometers !== BOOKING_KM_OPTIONS.UNLIMITED) {
      return BOOKING_KM_OPTIONS.KM_200
    }

    if (!isDefined(kilometers)) {
      return BOOKING_KM_OPTIONS.KM_100
    }

    return kilometers
  })

  return {
    queryDateFrom,
    queryDateFromCoerced,
    queryDateTo,
    queryDateToCoerced,
    queryHourFrom,
    queryHourFromCoerced,
    queryHourTo,
    queryHourToCoerced,
    queryKilometers,
    queryKilometersCoerced,
  }
}
